/**
 * Mock provider — the default backend.
 *
 * Implements every contract in ../contracts against the in-memory store, so
 * the whole app runs with no Firebase project, no server and no network.
 * This is the mode to use while screens are being built.
 *
 * Nothing persists. A page refresh resets the data.
 */

import { state, ME, subscribe, emit, latency } from './store.js'
import { toUser, toChat, toMessage, toStatus, toCall, DEFAULT_ICE_CONFIG } from '../../models/index.js'
import { newId, directChatId, tempMessageId } from '../../utils/id.js'
import { ZivError, ErrorCode } from '../errors.js'

const me = () => state.currentUserId

/** A direct chat is titled by the other person; a group by its name. */
function withPeople(chat) {
  // Participants carry the person, not just their id — see toChat.
  const participants = (chat.participants ?? []).map(p => {
    const u = state.users.get(p.userId)
    return u
      ? { ...p, displayName: u.displayName, avatarUrl: u.avatarUrl,
          about: u.about, isOnline: u.isOnline }
      : p
  })
  return { ...chat, participants }
}

function withPeer(chat) {
  chat = withPeople(chat)
  if (chat.type !== 'direct') return chat
  const otherId = (chat.participantIds ?? []).find(id => id !== me())
  const other = otherId ? state.users.get(otherId) : null
  return other
    ? { ...chat, peerName: other.displayName, peerAvatarUrl: other.avatarUrl }
    : chat
}

function chatList() {
  return [...state.chats.values()]
    .map(c => toChat(c.id, withPeer(c), me()))
    .sort((a, b) => (b.lastMessageAt?.getTime() ?? 0) - (a.lastMessageAt?.getTime() ?? 0))
}

function messageList(chatId) {
  return (state.messages.get(chatId) ?? [])
    .map(m => toMessage(m.id, m, chatId))
    .sort((a, b) => a.createdAt - b.createdAt)
}

function pushChatUpdate() { emit('chats', chatList()) }
function pushMessages(chatId) { emit(`messages:${chatId}`, messageList(chatId)) }

// ---------------------------------------------------------------------------

export const auth = {
  async requestCode(phone) {
    await latency()
    if (!phone) throw new ZivError(ErrorCode.INVALID_INPUT, 'Enter a phone number.')
    return { verificationId: `mock_${newId()}` }
  },

  async confirmCode(verificationId, code) {
    await latency()
    // Any 6 digits are accepted in mock mode
    if (!/^\d{6}$/.test(code)) throw new ZivError(ErrorCode.CODE_INVALID, 'That code is not correct.')
    state.isSignedIn = true
    const user = toUser(ME, state.users.get(ME))
    emit('auth', user)
    return user
  },

  async signOut() {
    await latency(60)
    state.isSignedIn = false
    emit('auth', null)
  },

  currentUser() {
    return state.isSignedIn ? toUser(ME, state.users.get(ME)) : null
  },

  onAuthChange(callback) {
    queueMicrotask(() => callback(this.currentUser()))
    return subscribe('auth', callback)
  },

  async updateProfile(patch) {
    await latency()
    const raw = state.users.get(me())
    Object.assign(raw, patch)
    const user = toUser(raw.id, raw)
    emit('auth', user)
    return user
  },

  async deleteAccount() {
    await latency()
    state.isSignedIn = false
    emit('auth', null)
  }
}

// ---------------------------------------------------------------------------

async function setFlag(chatId, key, value) {
  await latency(60)
  const chat = state.chats.get(chatId)
  chat.userState[me()] = { ...(chat.userState[me()] ?? {}), [key]: value }
  emit(`chat:${chatId}`)
  pushChatUpdate()
}

export const chats = {
  subscribeToList(callback) {
    /*
     * Recomputed here, not taken from the emit payload.
     *
     * This used to be `subscribe('chats', callback)`, which handed the
     * subscriber whatever the emitter passed — fine for pushChatUpdate(),
     * which passes the list, and `undefined` for a plain emit('chats').
     * Communities emit that way, from their own file, and the chat list
     * went undefined the moment a community group was created.
     *
     * A subscriber that recomputes cannot be broken by a new emitter
     * forgetting the convention, and the cost is one cheap map over a
     * handful of chats.
     */
    const send = () => callback(chatList())
    queueMicrotask(send)
    return subscribe('chats', send)
  },

  subscribeToChat(chatId, callback) {
    const send = () => {
      const raw = state.chats.get(chatId)
      callback(raw ? toChat(chatId, withPeer(raw), me()) : null)
    }
    queueMicrotask(send)
    return subscribe(`chat:${chatId}`, send)
  },

  async get(chatId) {
    await latency(60)
    const raw = state.chats.get(chatId)
    return raw ? toChat(chatId, withPeer(raw), me()) : null
  },

  async getOrCreateDirect(otherUserId) {
    await latency()
    const id = directChatId(me(), otherUserId)
    if (!state.chats.has(id)) {
      state.chats.set(id, {
        id,
        type: 'direct',
        name: null,
        description: null,
        avatarUrl: null,
        participantIds: [me(), otherUserId],
        participants: [
          { userId: me(), role: 'member', joinedAt: new Date() },
          { userId: otherUserId, role: 'member', joinedAt: new Date() }
        ],
        lastMessage: null,
        lastMessageAt: null,
        userState: {},
        createdBy: me(),
        createdAt: new Date()
      })
      state.messages.set(id, [])
      pushChatUpdate()
    }
    return toChat(id, withPeer(state.chats.get(id)), me())
  },

  async createGroup({ name, memberIds, avatarUrl = null }) {
    await latency()
    const id = `c_${newId()}`
    const participantIds = [me(), ...memberIds.filter(x => x !== me())]
    state.chats.set(id, {
      id,
      type: 'group',
      name,
      description: null,
      avatarUrl,
      participantIds,
      participants: participantIds.map(userId => ({
        userId,
        role: userId === me() ? 'admin' : 'member',
        joinedAt: new Date()
      })),
      lastMessage: null,
      lastMessageAt: null,
      userState: {},
      createdBy: me(),
      createdAt: new Date()
    })
    state.messages.set(id, [])
    pushChatUpdate()
    return toChat(id, withPeer(state.chats.get(id)), me())
  },

  async updateGroup(chatId, patch) {
    await latency()
    Object.assign(state.chats.get(chatId), patch)
    emit(`chat:${chatId}`)
    pushChatUpdate()
  },

  async addMembers(chatId, userIds) {
    await latency()
    const chat = state.chats.get(chatId)
    for (const userId of userIds) {
      if (chat.participantIds.includes(userId)) continue
      chat.participantIds.push(userId)
      chat.participants.push({ userId, role: 'member', joinedAt: new Date() })
    }
    emit(`chat:${chatId}`)
    pushChatUpdate()
  },

  async removeMember(chatId, userId) {
    await latency()
    const chat = state.chats.get(chatId)
    chat.participantIds = chat.participantIds.filter(x => x !== userId)
    chat.participants = chat.participants.filter(p => p.userId !== userId)
    emit(`chat:${chatId}`)
    pushChatUpdate()
  },

  async setMemberRole(chatId, userId, role) {
    await latency()
    const participant = state.chats.get(chatId).participants.find(p => p.userId === userId)
    if (participant) participant.role = role
    emit(`chat:${chatId}`)
  },

  async leaveGroup(chatId) {
    return chats.removeMember(chatId, me())
  },

  async setMuted(chatId, muted) {
    await setFlag(chatId, 'muted', muted)
  },

  async setPinned(chatId, pinned) {
    await setFlag(chatId, 'pinned', pinned)
  },

  async setArchived(chatId, archived) {
    await setFlag(chatId, 'archived', archived)
  },

  async clearMessages(chatId) {
    await latency()
    state.messages.set(chatId, [])
    const chat = state.chats.get(chatId)
    chat.lastMessage = null
    chat.lastMessageAt = null
    pushMessages(chatId)
    pushChatUpdate()
  },

  async deleteChat(chatId) {
    await latency()
    state.chats.delete(chatId)
    state.messages.delete(chatId)
    pushChatUpdate()
  }
}

// ---------------------------------------------------------------------------

export const messages = {
  subscribe(chatId, callback, { limit = 50 } = {}) {
    const send = () => callback(messageList(chatId).slice(-limit))
    queueMicrotask(send)
    return subscribe(`messages:${chatId}`, send)
  },

  async loadOlder(chatId, beforeMessageId, limit = 30) {
    await latency()
    const all = messageList(chatId)
    const index = all.findIndex(m => m.id === beforeMessageId)
    return index <= 0 ? [] : all.slice(Math.max(0, index - limit), index)
  },

  async send(chatId, { type, body = '', mediaUrl = null, mediaMeta = null, replyToId = null }) {
    // Insert locally as 'pending' first so the UI feels instant.
    const raw = {
      id: tempMessageId(),
      chatId,
      senderId: me(),
      type,
      body,
      mediaUrl,
      mediaMeta,
      replyToId,
      status: 'pending',
      reactions: {},
      createdAt: new Date(),
      editedAt: null,
      deletedAt: null
    }
    state.messages.get(chatId)?.push(raw)
    pushMessages(chatId)

    await latency(300)
    raw.id = newId()
    raw.status = 'sent'

    const chat = state.chats.get(chatId)
    chat.lastMessage = {
      id: raw.id,
      senderId: raw.senderId,
      type,
      preview: body,
      createdAt: raw.createdAt
    }
    chat.lastMessageAt = raw.createdAt

    pushMessages(chatId)
    pushChatUpdate()
    return toMessage(raw.id, raw, chatId)
  },

  async edit(chatId, messageId, body) {
    await latency()
    const message = state.messages.get(chatId)?.find(m => m.id === messageId)
    if (!message) throw new ZivError(ErrorCode.NOT_FOUND, 'Message not found.')
    message.body = body
    message.editedAt = new Date()
    pushMessages(chatId)
  },

  async remove(chatId, messageId, scope) {
    await latency()
    const list = state.messages.get(chatId) ?? []
    if (scope === 'everyone') {
      const message = list.find(m => m.id === messageId)
      if (message) {
        message.deletedAt = new Date()
        message.body = ''
        message.mediaUrl = null
      }
    } else {
      state.messages.set(chatId, list.filter(m => m.id !== messageId))
    }
    pushMessages(chatId)
  },

  async markRead(chatId, messageIds) {
    await latency(40)
    for (const message of state.messages.get(chatId) ?? []) {
      if (messageIds.includes(message.id)) message.status = 'read'
    }
    const chat = state.chats.get(chatId)
    chat.userState[me()] = { ...(chat.userState[me()] ?? {}), unreadCount: 0 }
    pushMessages(chatId)
    pushChatUpdate()
  },

  async react(chatId, messageId, emoji) {
    await latency(40)
    const message = state.messages.get(chatId)?.find(m => m.id === messageId)
    if (!message) return
    const existing = message.reactions[emoji] ?? []
    message.reactions[emoji] = [...new Set([...existing, me()])]
    pushMessages(chatId)
  },

  async unreact(chatId, messageId) {
    await latency(40)
    const message = state.messages.get(chatId)?.find(m => m.id === messageId)
    if (!message) return
    for (const emoji of Object.keys(message.reactions)) {
      const remaining = message.reactions[emoji].filter(id => id !== me())
      if (remaining.length) message.reactions[emoji] = remaining
      else delete message.reactions[emoji]
    }
    pushMessages(chatId)
  },

  async search(chatId, query) {
    await latency()
    const needle = query.toLowerCase()
    return messageList(chatId).filter(m => m.body.toLowerCase().includes(needle))
  }
}

// ---------------------------------------------------------------------------

export const media = {
  async upload(file, { onProgress } = {}) {
    for (let pct = 0; pct <= 100; pct += 20) {
      await latency(80)
      onProgress?.(pct)
    }
    return {
      url: URL.createObjectURL(file),
      path: `mock/${newId()}`,
      meta: { mimeType: file.type, sizeBytes: file.size, fileName: file.name }
    }
  },

  async remove() {
    await latency(60)
  },

  async localPreview(file) {
    return URL.createObjectURL(file)
  }
}

// ---------------------------------------------------------------------------

export const status = {
  subscribeToFeed(callback) {
    const send = () => callback(
      [...state.statuses.values()]
        .filter(s => s.userId !== me())
        .map(s => toStatus(s.id, s, me()))
    )
    queueMicrotask(send)
    return subscribe('status', send)
  },

  subscribeToMine(callback) {
    const send = () => callback(
      [...state.statuses.values()]
        .filter(s => s.userId === me())
        .map(s => toStatus(s.id, s, me()))
    )
    queueMicrotask(send)
    return subscribe('status', send)
  },

  async post({ type, body = '', mediaUrl = null, backgroundColor = null, font = null }) {
    await latency()
    const id = `s_${newId()}`
    const createdAt = new Date()
    state.statuses.set(id, {
      id,
      userId: me(),
      type,
      body,
      mediaUrl,
      backgroundColor,
      font,
      viewCount: 0,
      viewedBy: [],
      createdAt,
      expiresAt: new Date(createdAt.getTime() + 24 * 60 * 60 * 1000)
    })
    emit('status')
    return toStatus(id, state.statuses.get(id), me())
  },

  async markViewed(statusId) {
    await latency(40)
    const update = state.statuses.get(statusId)
    if (!update || update.viewedBy.includes(me())) return
    update.viewedBy.push(me())
    update.viewCount += 1
    emit('status')
  },

  async getViewers(statusId) {
    await latency()
    const viewers = state.statuses.get(statusId)?.viewedBy ?? []
    return viewers.map(userId => ({ userId, viewedAt: new Date() }))
  },

  async remove(statusId) {
    await latency()
    state.statuses.delete(statusId)
    emit('status')
  }
}

// ---------------------------------------------------------------------------

export const presence = {
  startHeartbeat() {
    state.users.get(me()).isOnline = true
    return () => {
      state.users.get(me()).isOnline = false
    }
  },

  watch(userIds, callback) {
    const send = () => {
      const map = {}
      for (const id of userIds) {
        const user = state.users.get(id)
        map[id] = { isOnline: user?.isOnline ?? false, lastSeen: user?.lastSeen ?? null }
      }
      callback(map)
    }
    queueMicrotask(send)
    return subscribe('presence', send)
  },

  setTyping(chatId, isTyping) {
    if (!state.typing.has(chatId)) state.typing.set(chatId, new Set())
    const set = state.typing.get(chatId)
    if (isTyping) set.add(me())
    else set.delete(me())
    emit(`typing:${chatId}`, [...set].filter(id => id !== me()))
  },

  watchTyping(chatId, callback) {
    queueMicrotask(() => callback([]))
    return subscribe(`typing:${chatId}`, callback)
  }
}

// ---------------------------------------------------------------------------

async function setCallStatus(callId, callStatus, patch = {}) {
  await latency(60)
  const call = state.calls.get(callId)
  if (!call) return
  Object.assign(call, { status: callStatus, ...patch })
  emit(`call:${callId}`)
  emit('calls')
}

export const calls = {
  async start({ chatId, calleeIds, type }) {
    await latency()
    const id = `call_${newId()}`
    state.calls.set(id, {
      id,
      chatId,
      callerId: me(),
      calleeIds,
      type,
      status: 'ringing',
      createdAt: new Date(),
      answeredAt: null,
      endedAt: null
    })
    emit('calls')
    return toCall(id, state.calls.get(id))
  },

  async accept(callId) {
    await setCallStatus(callId, 'connected', { answeredAt: new Date() })
  },

  async decline(callId) {
    await setCallStatus(callId, 'declined', { endedAt: new Date() })
  },

  async end(callId) {
    await setCallStatus(callId, 'ended', { endedAt: new Date() })
  },

  subscribeToIncoming(callback) {
    return subscribe('calls:incoming', callback)
  },

  subscribeToCall(callId, callback) {
    const send = () => {
      const raw = state.calls.get(callId)
      if (raw) callback(toCall(callId, raw))
    }
    queueMicrotask(send)
    return subscribe(`call:${callId}`, send)
  },

  // Signalling is a no-op here; in mock mode there is no second peer.
  async sendOffer() {
    await latency(40)
  },

  async sendAnswer() {
    await latency(40)
  },

  async sendCandidate() {
    await latency(40)
  },

  subscribeToSignals() {
    return () => {}
  },

  subscribeToHistory(callback) {
    const send = () => callback([...state.calls.values()].map(c => toCall(c.id, c)))
    queueMicrotask(send)
    return subscribe('calls', send)
  },

  getIceConfig() {
    return DEFAULT_ICE_CONFIG
  }
}

// ---------------------------------------------------------------------------

export const contacts = {
  subscribe(callback) {
    const send = () => callback([...state.contacts.values()])
    queueMicrotask(send)
    return subscribe('contacts', send)
  },

  async findByPhone(phone) {
    await latency()
    const found = [...state.users.values()].find(u => u.phone === phone)
    return found ? toUser(found.id, found) : null
  },

  async discover(phones) {
    await latency()
    return [...state.users.values()]
      .filter(u => phones.includes(u.phone) && u.id !== me())
      .map(u => toUser(u.id, u))
  },

  async add({ userId, savedName }) {
    await latency()
    const contact = {
      id: `ct_${newId()}`,
      userId,
      savedName,
      user: toUser(userId, state.users.get(userId))
    }
    state.contacts.set(userId, contact)
    emit('contacts')
    return contact
  },

  async remove(contactId) {
    await latency()
    for (const [key, contact] of state.contacts) {
      if (contact.id === contactId) state.contacts.delete(key)
    }
    emit('contacts')
  },

  async block(userId) {
    await latency(60)
    state.blocked.add(userId)
    emit('blocked', [...state.blocked])
  },

  async unblock(userId) {
    await latency(60)
    state.blocked.delete(userId)
    emit('blocked', [...state.blocked])
  },

  subscribeToBlocked(callback) {
    queueMicrotask(() => callback([...state.blocked]))
    return subscribe('blocked', callback)
  }
}

// Channels live in their own file: see the privacy note at the top of it.
export { channels } from './channels.js'

// Communities live in their own file, like channels: see the note at its top.
export { communities } from './communities.js'
